import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useAuth } from './auth'

export interface FeedEvent {
  id: string
  type: string
  payload: Record<string, unknown>
}

type Subscriber = (event: FeedEvent) => void

const EventFeedContext = createContext<((subscriber: Subscriber) => () => void) | undefined>(undefined)

/** One authenticated, audience-scoped long-poll for the entire SPA. Feature
 * subscribers filter locally, so Chat and the bell never open competing feed
 * loops and no interval poll is needed. */
export function EventFeedProvider({ children }: { children: ReactNode }) {
  const { authFetch, status } = useAuth()
  const subscribers = useRef(new Set<Subscriber>())
  const subscribe = useCallback((subscriber: Subscriber) => {
    subscribers.current.add(subscriber)
    return () => subscribers.current.delete(subscriber)
  }, [])

  useEffect(() => {
    if (status !== 'authenticated') return
    let stopped = false
    const controller = new AbortController()

    // FF-QA-005 / FF-QA-103: a dead API used to be retried every five seconds
    // for as long as the tab lived (one health probe per retry with it). The
    // wait doubles per failure up to a minute and resets on the next answer.
    let failures = 0
    async function backoff() {
      failures += 1
      await new Promise((resolve) => window.setTimeout(resolve, Math.min(5_000 * 2 ** (failures - 1), 60_000)))
    }

    async function run() {
      let cursor: string | undefined
      // Bootstrap RETRIES rather than giving up (audit RUN-10). Both exits here
      // used to `return` out of run() before the polling loop was ever entered,
      // so a single transient 429/500/offline moment at mount silently killed
      // real-time for the WHOLE SPA for the rest of the session — chat, the
      // notification bell, and the settings/modules subscriptions all went dead
      // with no indication to the user. The polling loop below always backed
      // off correctly; only the bootstrap did not.
      while (!stopped && cursor === undefined) {
        try {
          // Bootstrap directly to the audience-visible head. Replaying the
          // retained outbox can exhaust rate limits on mature tenants and would
          // make old notification/chat events look live again.
          const res = await authFetch('/api/v1/events?tail=true&limit=1', {
            signal: controller.signal,
          })
          if (!res.ok) {
            await backoff()
            continue
          }
          const json = (await res.json()) as { data: FeedEvent[]; cursor: string }
          cursor = json.cursor
          failures = 0
        } catch {
          if (stopped) return
          await backoff()
        }
      }
      if (stopped) return

      while (!stopped) {
        try {
          const res = await authFetch(`/api/v1/events?cursor=${cursor}&limit=200`, {
            signal: controller.signal,
          })
          if (!res.ok) {
            await backoff()
            continue
          }
          const json = (await res.json()) as { data: FeedEvent[]; cursor: string }
          cursor = json.cursor
          failures = 0
          for (const event of json.data) {
            for (const subscriber of subscribers.current) subscriber(event)
          }
        } catch {
          if (stopped) return
          await backoff()
        }
      }
    }

    void run()
    return () => {
      stopped = true
      controller.abort()
    }
  }, [authFetch, status])

  return <EventFeedContext.Provider value={subscribe}>{children}</EventFeedContext.Provider>
}

function matches(type: string, patterns: string[]): boolean {
  return patterns.some((pattern) =>
    pattern.endsWith('*') ? type.startsWith(pattern.slice(0, -1)) : type === pattern,
  )
}

export function useEventSubscription(patterns: string[], onEvent: Subscriber): void {
  const subscribe = useContext(EventFeedContext)
  const handler = useRef(onEvent)
  handler.current = onEvent
  const patternKey = patterns.join('\u0000')
  const stablePatterns = useMemo(() => patternKey.split('\u0000'), [patternKey])

  useEffect(() => {
    if (!subscribe) return
    return subscribe((event) => {
      if (matches(event.type, stablePatterns)) handler.current(event)
    })
  }, [stablePatterns, subscribe])
}
